"use client";

import type { AccountType } from "./events";
import type { PersonUpdate } from "./client";

/**
 * Person properties for PostHog profiles (ANALYTICS_SPEC §3).
 *
 * Caveat, stated plainly: `total_designs_completed` is counted in localStorage,
 * so it is per-device and resets when the user clears their cache. Treat it as a
 * segmentation/filtering convenience only. The authoritative number is a
 * count-of-`design_completed`-per-person insight in PostHog, which is what the
 * spec itself recommends.
 *
 * `first_design_date` uses `$set_once`, so the earliest value wins even after an
 * anonymous visitor later logs in and PostHog merges the two profiles.
 */

const COUNT_KEY = "ph_designs_completed";
const FIRST_DATE_KEY = "ph_first_design_date";

function readCount(): number {
  try {
    const raw = window.localStorage.getItem(COUNT_KEY);
    const n = raw ? Number.parseInt(raw, 10) : 0;
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return 0; // private browsing / storage disabled
  }
}

/**
 * Call when a design reaches the result screen. Increments the local counter and
 * returns the person update to pass as `capture()`'s third argument.
 */
export function markDesignCompleted(accountType: AccountType): PersonUpdate {
  const now = new Date().toISOString();

  if (typeof window === "undefined") {
    return {
      set: { account_type: accountType },
      setOnce: { first_design_date: now },
    };
  }

  const total = readCount() + 1;
  let firstDate = now;
  try {
    window.localStorage.setItem(COUNT_KEY, String(total));
    firstDate = window.localStorage.getItem(FIRST_DATE_KEY) || now;
    if (!window.localStorage.getItem(FIRST_DATE_KEY)) {
      window.localStorage.setItem(FIRST_DATE_KEY, now);
    }
  } catch {
    // Storage unavailable — still send what we have.
  }

  return {
    set: { account_type: accountType, total_designs_completed: total },
    setOnce: { first_design_date: firstDate },
  };
}
